import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import TpDialog from '../shared/tp_dialog';
import { fetch_email_preview } from '../api';
import { SENDING_NOT_ENABLED_TEXT, email_error_view, preview_view } from './adapters';

/*
 * PROD-7828 R11 — one teacher-month's email preview. The iframe is sandboxed with no scripts and no
 * same-origin, and renders the server's HTML byte-for-byte (brief section 13.2) — never re-derived here.
 * The teacher's real address is never requested or shown; "To" is always the server's test recipient.
 */
const LOAD_FAILED_TEXT = 'Could not load the email preview.';

const PreviewDialog = ({ pay_month, month_label, teacher_uuid, teacher_name, on_close, on_focus_lost, on_error }) => {
  const [state, setState] = useState('loading'); // loading | ready | error
  const [view, setView] = useState(null);
  const [error_text, setErrorText] = useState('');
  const seq = useRef(0);
  const on_close_ref = useRef(on_close);
  on_close_ref.current = on_close;
  const on_error_ref = useRef(on_error);
  on_error_ref.current = on_error;

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setState('loading');
    fetch_email_preview(pay_month, teacher_uuid)
      .then((data) => {
        if (mine !== seq.current) return;
        setView(preview_view(data));
        setState('ready');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = email_error_view(e, LOAD_FAILED_TEXT);
        if (v.kind === 'permission') {
          on_error_ref.current(e, LOAD_FAILED_TEXT);
          on_close_ref.current();
          return;
        }
        setErrorText(v.text);
        setState('error');
      });
  }, [pay_month, teacher_uuid]);

  useEffect(() => {
    load();
  }, [load]);

  const title = `${teacher_name || 'Teacher'} · ${month_label}`;

  return (
    <TpDialog title={title} on_close={on_close} on_focus_lost={on_focus_lost}>
      <div className="tp-em-preview">
        {state === 'loading' ? <CustomLoading /> : null}
        {state === 'error' ? <LoadFailed text={error_text} on_retry={load} /> : null}
        {state === 'ready' ? (
          <>
            <div className="tp-banner tp-banner-warn" role="alert">
              Test mode — every payment email goes to Kuldeep, never to teachers.
            </div>
            {view.is_draft ? (
              <div className="tp-banner tp-banner-info">
                Draft: this month is not closed, so the figures may still change.
              </div>
            ) : null}
            {!view.can_send ? <div className="tp-hint">{SENDING_NOT_ENABLED_TEXT}</div> : null}
            {view.blockers.length ? (
              <ul className="tp-em-blockers">
                {view.blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            ) : null}
            <dl className="tp-em-meta">
              <dt>To</dt>
              <dd>{view.to_display || 'Not configured'}</dd>
              <dt>Subject</dt>
              <dd>{view.subject}</dd>
            </dl>
            <iframe
              title={`Email preview for ${teacher_name || 'teacher'}`}
              className="tp-em-frame"
              sandbox=""
              srcDoc={view.html}
            />
          </>
        ) : null}
      </div>
    </TpDialog>
  );
};

export default PreviewDialog;
